import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { Stars, StarPicker, VerifiedBadge } from "../components/Stars";

export default function Feedback() {
  const [reviews, setReviews] = useState([]);
  const [summary, setSummary] = useState({ average: 0, count: 0 });
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [rating, setRating] = useState(0);
  const [message, setMessage] = useState("");
  const [website, setWebsite] = useState("");
  const [sending, setSending] = useState(false);

  const load = async () => {
    try {
      const [r, s] = await Promise.all([api.get("/feedback"), api.get("/feedback/summary")]);
      setReviews(r.data);
      setSummary(s.data);
    } catch {
      // silent
    }
  };

  useEffect(() => {
    load();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!rating) {
      toast.error("Please pick a star rating (1–5).");
      return;
    }
    setSending(true);
    try {
      await api.post("/feedback", { name, email, rating, message, website });
      toast.success("Thanks for your feedback!");
      setName("");
      setEmail("");
      setRating(0);
      setMessage("");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid="feedback-page" className="px-6 md:px-12 lg:px-20 py-20 md:py-28">
      {/* Header + summary */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-16">
        <div className="md:col-span-7">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#71717A] mb-4">
            Customer feedback
          </p>
          <h1 className="font-display font-black text-5xl md:text-7xl leading-[0.9]">
            How did
            <br />
            <span className="relative inline-block">
              we do
              <span className="absolute -bottom-1 left-0 right-0 h-3 bg-[#FFD600] -z-10" />
            </span>
            <span className="text-[#E6007A]">?</span>
          </h1>
        </div>
        <div className="md:col-span-5">
          {summary.count > 0 ? (
            <div data-testid="feedback-summary" className="border border-black/15 bg-white p-6 inline-flex items-center gap-5">
              <span className="font-display font-black text-6xl leading-none">{summary.average}</span>
              <div>
                <Stars value={summary.average} size={22} testid="summary-stars" />
                <p className="font-mono text-xs text-[#71717A] mt-2 uppercase tracking-[0.2em]">
                  from {summary.count} review{summary.count === 1 ? "" : "s"}
                </p>
              </div>
            </div>
          ) : (
            <p data-testid="feedback-summary-empty" className="font-mono text-sm text-[#71717A]">
              No reviews yet — be the first to rate our service.
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        {/* Form */}
        <form
          onSubmit={submit}
          data-testid="feedback-form"
          className="lg:col-span-5 border border-black/15 bg-white p-6 md:p-8 space-y-5 self-start"
        >
          <h2 className="font-display font-black text-2xl">Leave your feedback</h2>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A] mb-2">
              Your name
            </label>
            <input
              value={name}
              required
              maxLength={80}
              data-testid="feedback-name-input"
              onChange={(e) => setName(e.target.value)}
              className="w-full border border-black/15 px-4 py-3 font-mono text-sm focus:outline-none focus:border-black rounded-none"
              placeholder="Jane Doe"
            />
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A] mb-2">
              Email <span className="normal-case tracking-normal">(optional)</span>
            </label>
            <input
              type="email"
              value={email}
              maxLength={120}
              data-testid="feedback-email-input"
              onChange={(e) => setEmail(e.target.value)}
              className="w-full border border-black/15 px-4 py-3 font-mono text-sm focus:outline-none focus:border-black rounded-none"
              placeholder="you@example.com"
            />
            <p className="font-mono text-[10px] text-[#71717A] mt-2 leading-relaxed">
              Use the email from your shop order to earn a <span className="text-[#0E7490]">Verified buyer</span> badge. Never shown publicly.
            </p>
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A] mb-2">
              Your rating
            </label>
            <StarPicker value={rating} onChange={setRating} testid="feedback-star-picker" />
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A] mb-2">
              Your feedback
            </label>
            <textarea
              rows={4}
              value={message}
              required
              maxLength={1000}
              data-testid="feedback-message-input"
              onChange={(e) => setMessage(e.target.value)}
              className="w-full border border-black/15 px-4 py-3 font-mono text-sm focus:outline-none focus:border-black rounded-none resize-none"
              placeholder="Tell us about the service you received…"
            />
          </div>
          {/* Honeypot — hidden from humans */}
          <input
            type="text"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            className="absolute -left-[9999px] w-px h-px opacity-0"
            placeholder="Website"
          />
          <button
            type="submit"
            disabled={sending}
            data-testid="feedback-submit-button"
            className="btn-ink w-full bg-black text-white px-6 py-4 font-mono text-xs uppercase tracking-[0.25em] disabled:opacity-50"
          >
            {sending ? "Sending…" : "Submit feedback →"}
          </button>
        </form>

        {/* Reviews list */}
        <div className="lg:col-span-7 space-y-4" data-testid="feedback-list">
          {reviews.length === 0 && (
            <p className="font-mono text-sm text-[#71717A] py-16 text-center border border-dashed border-black/15">
              No feedback yet.
            </p>
          )}
          {reviews.map((r, i) => (
            <motion.div
              key={r.id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: Math.min(i * 0.05, 0.4) }}
              data-testid={`feedback-item-${r.id}`}
              className="border border-black/15 bg-white p-6"
            >
              <div className="flex items-start justify-between gap-4 flex-wrap mb-3">
                <div>
                  <p className="font-display font-black text-lg flex items-center gap-2 flex-wrap">
                    {r.name} {r.verified_buyer && <VerifiedBadge />}
                  </p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#71717A]">
                    {new Date(r.created_at).toLocaleDateString()}
                  </p>
                </div>
                <Stars value={r.rating} size={18} testid={`feedback-stars-${r.id}`} />
              </div>
              <p className="font-mono text-sm leading-relaxed text-[#27272A]">{r.message}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}
